import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Helpers } from '../helpers/Helpers';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

@Injectable({
  providedIn: 'root'
})
export class GoogleAnalyticsService {
  private initialized = false;
  private lastUserId: string = null;

  constructor(
    private router: Router,
    private helper: Helpers
  ) {}

  init(): void {
    if (!environment.gaEnabled || !environment.gaMeasurementId || this.initialized) {
      return;
    }

    this.loadGtagScript(environment.gaMeasurementId);
    this.initialized = true;

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.trackPageView(event.urlAfterRedirects);
    });
  }

  trackPageView(url: string): void {
    if (!this.canTrack()) {
      return;
    }

    this.syncUserId();

    window.gtag('event', 'page_view', {
      page_path: this.sanitizePath(url),
      page_location: window.location.origin + this.sanitizePath(url),
      page_title: document.title
    });
  }

  private syncUserId(): void {
    const userId = this.helper.userId;
    if (!userId || userId === this.lastUserId) {
      return;
    }

    this.lastUserId = userId;
    window.gtag('config', environment.gaMeasurementId, {
      user_id: userId,
      send_page_view: false
    });
  }

  private canTrack(): boolean {
    return environment.gaEnabled
      && !!environment.gaMeasurementId
      && typeof window !== 'undefined'
      && typeof window.gtag === 'function';
  }

  /**
   * Collapse numeric / UUID route segments so GA groups pages
   * (e.g. /admin/project-boq/123 → /admin/project-boq/:id)
   * and avoids leaking identifiers into Analytics.
   */
  private sanitizePath(url: string): string {
    const path = (url || '/').split('?')[0].split('#')[0] || '/';
    return path
      .replace(/\/\d+/g, '/:id')
      .replace(
        /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
        '/:id'
      );
  }

  private loadGtagScript(measurementId: string): void {
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function() {
      window.dataLayer.push(arguments);
    };

    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      send_page_view: false
    });

    const existing = document.querySelector(`script[src*="gtag/js?id=${measurementId}"]`);
    if (existing) {
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);
  }
}
